import { useState } from "react"

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import { CheckIcon } from "@heroicons/react/20/solid"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { startOfMonth, startOfToday, startOfTomorrow, startOfYear, subDays } from "date-fns"

import { Bee } from "~/src/components/BeeKit"

function createDateRangeList(): DateRange[] {
  const now = new Date()

  const startOfThisMonth = startOfMonth(now)
  const startOfLastMonth = startOfMonth(subDays(startOfThisMonth, 1))
  const startOfThisYear = startOfYear(now)
  const startOfLastYear = startOfYear(subDays(startOfThisYear, 1))

  // 6 days ago at midnight
  const daysAgo6 = subDays(startOfToday(), 6)
  // 29 days ago at midnight
  const daysAgo29 = subDays(startOfToday(), 29)
  return [
    { label: "Today", start: startOfToday(), end: startOfTomorrow() },
    { label: "Last 7 days", start: daysAgo6, end: startOfTomorrow() }, // 6 days + today
    { label: "Last 30 days", start: daysAgo29, end: startOfTomorrow(), default: true }, // 29 days + today
    { label: "This month", start: startOfThisMonth, end: startOfTomorrow() },
    { label: "Last month", start: startOfLastMonth, end: startOfThisMonth }, // Full last month
    { label: "This year", start: startOfThisYear, end: startOfTomorrow() }, // YTD
    { label: "Last year", start: startOfLastYear, end: startOfThisYear }, // Full last year
    { label: "All Time", allTime: true },
  ]
}

//region Types
type DateRangeFilterDesktopProps = {
  dateRangeList: DateRange[]
  dateRangeIndex: number
  onUpdate: (newIndex: number) => void
}

type DateRangeFilterMobileProps = DateRangeFilterDesktopProps & {
  close: () => void
}

export type DateRangeFilterProps = {
  dateRangeList?: DateRange[]
  startDate?: Date | null
  endDate?: Date | null
  allTime?: boolean
  onChange: (startDate: Date | null, endDate: Date | null, allTime: boolean) => void
}

export type DateRange = {
  label: React.ReactNode
  start?: Date
  end?: Date
  allTime?: boolean
  default?: boolean
}

function dayMatches(lhs: Date | null, rhs: Date | null): boolean {
  const lhsString = lhs?.toISOString().split("T")[0]
  const rhsString = rhs?.toISOString().split("T")[0]
  return lhsString == rhsString
}

//endregion
function dateRangeMatches(dateRange: DateRange, start: Date | null, end: Date | null, allTime: boolean): boolean {
  if (allTime) {
    return dateRange.allTime ?? false
  }

  const startMatches = dayMatches(dateRange.start || null, start)
  const endMatches = dayMatches(dateRange.end || null, end)

  return !dateRange.allTime && startMatches && endMatches
}

//region Component
// Expects to be inside a <MenuItems> component
function DateRangeFilterDesktop(props: DateRangeFilterDesktopProps) {
  const { dateRangeList, dateRangeIndex, onUpdate } = props

  return (
    <div className="relative z-10 hidden sm:block">
      <div className="absolute top-2 min-w-[175px] rounded-lg border bg-white">
        <div className="flex flex-col divide-y">
          {dateRangeList.map(({ label }, index) => (
            <MenuItem key={`date-range-desktop-${index}`}>
              <button
                className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:cursor-pointer"
                onClick={() => onUpdate(index)}
              >
                {label}
                {dateRangeIndex == index && <CheckIcon className="text-navy-800 h-4 w-4" />}
              </button>
            </MenuItem>
          ))}
        </div>
      </div>
    </div>
  )
}

// Expects to be inside a <MenuItems> component
function DateRangeFilterMobile(props: DateRangeFilterMobileProps) {
  const { close } = props
  const { dateRangeList, dateRangeIndex, onUpdate } = props
  const [selectedDateRangeIndex, setSelectedDateRangeIndex] = useState<number>(dateRangeIndex)

  return (
    <Bee.SelectionPanel
      className="sm:hidden"
      title="Date Range"
      onClose={() => {
        close()
      }}
      onApply={() => onUpdate(selectedDateRangeIndex)}
      disabled={dateRangeIndex == selectedDateRangeIndex}
    >
      {dateRangeList.map(({ label }, index) => (
        <label className="flex items-center gap-2" key={`date-range-mobile-${index}`}>
          <input
            type="radio"
            value={index}
            checked={selectedDateRangeIndex == index}
            onChange={() => setSelectedDateRangeIndex(index)}
          />
          {label}
        </label>
      ))}
    </Bee.SelectionPanel>
  )
}

export function DateRangeFilter(props: DateRangeFilterProps) {
  const { dateRangeList = createDateRangeList(), startDate, endDate, allTime, onChange } = props

  // preferIndex is used when multiple date ranges match,
  // ie on the 7th of the month, "Last 7 Days" and "This Month" both match
  // In those cases, preferIndex is used to prefer the most recent user choice
  const [preferIndex, setPreferIndex] = useState(-1)

  let dateRangeIndex = -1
  if (
    preferIndex >= 0 &&
    dateRangeMatches(dateRangeList[preferIndex], startDate || null, endDate || null, allTime || false)
  ) {
    dateRangeIndex = preferIndex
  } else {
    const matchingIndex = dateRangeList.findIndex((dateRange) =>
      dateRangeMatches(dateRange, startDate || null, endDate || null, allTime || false)
    )
    if (matchingIndex >= 0) {
      dateRangeIndex = matchingIndex
    } else {
      const defaultIndex = dateRangeList.findIndex((dateRange) => dateRange.default == true)
      if (defaultIndex >= 0) {
        dateRangeIndex = defaultIndex
      }
    }
  }

  //region Event Handlers
  const onUpdate = (newIndex: number) => {
    const { start, end, allTime } = dateRangeList[newIndex] || {}
    setPreferIndex(newIndex)
    onChange(start || null, end || null, allTime || false)
  }

  //region JSX
  return (
    <Menu>
      {({ close }) => (
        <div>
          <MenuButton
            as={Bee.Button}
            className="flex w-full items-center justify-between gap-2 font-normal"
            kind="secondary"
          >
            {dateRangeList[dateRangeIndex]?.label ?? "Custom range"}
            <ChevronDownIcon className="size-4 text-gray-900" />
          </MenuButton>
          <MenuItems modal={false}>
            <DateRangeFilterDesktop dateRangeIndex={dateRangeIndex} dateRangeList={dateRangeList} onUpdate={onUpdate} />
            <DateRangeFilterMobile
              close={close}
              dateRangeIndex={dateRangeIndex}
              dateRangeList={dateRangeList}
              onUpdate={onUpdate}
            />
          </MenuItems>
        </div>
      )}
    </Menu>
  )
  //endregion
}
//endregion

//region Private
//endregion
